import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import NotificationsAPI from "../../api/NotificationsAPI";
import useUser from "../auth/useUser";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";

interface Props {
  notificationInfo: {
    NotificationIDs: string[];
    RecordComments: { [key: string]: number };
  };
}

export default function useReadNotifications() {
  const client = useQueryClient();
  const { user } = useUser();
  const tenantID = useAtomValue(tenantIDAtom);

  const readNotifications = useMutation({
    mutationFn: ({ notificationInfo }: Props) =>
      NotificationsAPI.readNotifications(notificationInfo),
    onMutate: ({ notificationInfo }) => {
      client.setQueriesData(
        { queryKey: ["notifications", tenantID, user?._id] },
        (prev: any) => {
          if (!prev || !prev?.data?.length) return prev;

          const newNotifications = prev.data.map((n: any) => {
            if (notificationInfo.NotificationIDs.includes(n._id)) {
              n.Read = true;
            }

            return { ...n };
          });

          return {
            ...prev,
            data: newNotifications,
          };
        }
      );

      client.setQueriesData({ queryKey: ["user", tenantID] }, (prev: any) => {
        if (!tenantID) return prev;

        const keys = Object.keys(notificationInfo).filter(
          (key) => key !== "NotificationIDs"
        );

        if (!keys.length) return prev;

        const updatedUser = { ...(prev?.user || {}) };
        let notifications = structuredClone(updatedUser.Notifications);

        for (const key of keys) {
          if (key === "RecordComments") {
            for (const recordID in notificationInfo.RecordComments) {
              const updatedNotificationsNumber =
                (notifications[tenantID].RecordComments[recordID] ?? 0) -
                (notificationInfo.RecordComments[recordID] ?? 0);

              notifications[tenantID].RecordComments[recordID] =
                updatedNotificationsNumber >= 0
                  ? updatedNotificationsNumber
                  : 0;
            }
          }
        }

        return {
          ...(prev || {}),
          user: {
            ...updatedUser,
            Notifications: notifications,
          },
        };
      });
    },
    onError: (err) => console.log(err),
  });

  return { readNotifications: readNotifications.mutate };
}
